import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Box, 
  TextField, 
  Grid, 
  Card, 
  CardContent, 
  CardMedia, 
  Button, 
  Pagination, 
  Select, 
  MenuItem, 
  FormControl, 
  InputLabel, 
  Chip, 
  CircularProgress, 
  FormControlLabel, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  IconButton, 
  List, 
  ListItem, 
  ListItemText,
  ListItemIcon,
  Paper,
  Container,
  InputAdornment,
  Switch,
  alpha,
  LinearProgress
} from '@mui/material';
import { Link, Navigate } from 'react-router-dom';
import { getUserCourses } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import PhotoIcon from '@mui/icons-material/Photo';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import CloseIcon from '@mui/icons-material/Close';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import SearchIcon from '@mui/icons-material/Search';
import CategoryIcon from '@mui/icons-material/Category';
import SortIcon from '@mui/icons-material/Sort';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SchoolIcon from '@mui/icons-material/School';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ImageIcon from '@mui/icons-material/Image';
import OndemandVideoIcon from '@mui/icons-material/OndemandVideo';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PersonIcon from '@mui/icons-material/Person';
import MenuBookIcon from '@mui/icons-material/MenuBook';

// Paleta profesional estilo LinkedIn Learning / Coursera
const COLORS = {
  gradient1: '#00b894',      // Verde esmeralda
  gradient2: '#0984e3',      // Azul brillante
  primary: '#0984e3',        // Azul principal
  success: '#00b894',        // Verde éxito
  warning: '#eb9800ff',        // Amarillo cálido
  error: '#d63031',          // Rojo error
  info: '#74b9ff',           // Azul claro
  purple: '#6c5ce7',         // Violeta
  orange: '#e17055',         // Naranja coral
  dark: '#2d3436',           // Gris oscuro texto
  light: '#dfe6e9'           // Gris claro fondos
};

// Colores por categoría
const CATEGORY_COLORS = {
  'Mantenimiento': { color: COLORS.warning, icon: '🔧' },
  'Producción': { color: COLORS.success, icon: '🏭' },
  'Proceso': { color: COLORS.purple, icon: '⚙️' },
  'Calidad': { color: COLORS.primary, icon: '✅' },
  'General': { color: COLORS.orange, icon: '📖' }
};

const CursosPerfilPage = () => {
  const [courses, setCourses] = useState([]);
  const [filteredCourses, setFilteredCourses] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [sortOrder, setSortOrder] = useState('desc');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [hasAttachments, setHasAttachments] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedMaterials, setSelectedMaterials] = useState(null);
  const [fullscreen, setFullscreen] = useState(false);
  const { user } = useAuth();
  const coursesPerPage = 8;

  useEffect(() => {
    const fetchCourses = async () => {
      if (user && user.userId) {
        try {
          setLoading(true);
          const response = await getUserCourses(user.userId);
          const coursesArray = Array.isArray(response) ? response : [];
          setCourses(coursesArray);
        } catch (error) {
          console.error('Error fetching user courses:', error);
          setCourses([]);
        } finally {
          setLoading(false);
        }
      }
    };
    fetchCourses();
  }, [user]);

  useEffect(() => {
    if (courses.length > 0) {
      let filtered = courses.filter(course => 
        course.title.toLowerCase().includes(searchTerm.toLowerCase()) &&
        (selectedCategory === '' || course.category === selectedCategory) &&
        (!hasAttachments || hasValidAttachments(course))
      );
    
      filtered.sort((a, b) => {
        const dateA = new Date(a.created_at);
        const dateB = new Date(b.created_at);
        return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
      });
    
      setFilteredCourses(filtered);
    } else {
      setFilteredCourses([]);
    }
  }, [courses, searchTerm, selectedCategory, sortOrder, hasAttachments]);

  const hasValidAttachments = (course) => {
    return (course.course_pdf_path && course.course_pdf_path !== '[]') ||
           (course.course_image_path && course.course_image_path !== '[]') ||
           (course.course_video_path && course.course_video_path !== '[]');
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const paginatedCourses = filteredCourses.slice((page - 1) * coursesPerPage, page * coursesPerPage);

  const getFileUrl = (path) => {
    if (!path || path === '[]') return null;
    if (typeof path === 'string') {
      try {
        const parsedPath = JSON.parse(path);
        if (Array.isArray(parsedPath) && parsedPath.length > 0) {
          return parsedPath.map(p => `${process.env.REACT_APP_API_URL.replace('/api', '')}/${p.replace(/\\/g, '/')}`);
        }
      } catch (e) {
        return [`${process.env.REACT_APP_API_URL.replace('/api', '')}/${path.replace(/\\/g, '/')}`];
      }
    }
    return null;
  };

  const handleOpenMaterials = (materials, type) => {
    setSelectedMaterials({ files: materials, type });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedMaterials(null);
    setFullscreen(false);
  };

  const toggleFullscreen = () => setFullscreen(!fullscreen);

  const getCategoryTheme = (category) => {
    return CATEGORY_COLORS[category] || { color: COLORS.primary, icon: '📚' };
  };

  const selectStyles = (color) => ({
    borderRadius: 2,
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: alpha(color, 0.3),
      borderWidth: 2
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: color
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: color,
      borderWidth: 2
    }
  });

  const menuProps = (color) => ({
    PaperProps: {
      sx: {
        borderRadius: 2,
        mt: 0.5,
        boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
        '& .MuiMenuItem-root': {
          py: 1.5,
          px: 2,
          mx: 0.5,
          my: 0.25,
          borderRadius: 1.5,
          transition: 'all 0.2s ease',
          '&:hover': {
            backgroundColor: alpha(color, 0.1),
            transform: 'translateX(4px)'
          },
          '&.Mui-selected': {
            backgroundColor: alpha(color, 0.15)
          }
        }
      }
    }
  });

  const renderMaterialContent = () => {
    if (!selectedMaterials || !selectedMaterials.files) return null;
  
    const getIcon = (type) => {
      switch(type) {
        case 'pdf': return <InsertDriveFileIcon sx={{ color: COLORS.error }} />;
        case 'image': return <ImageIcon sx={{ color: COLORS.success }} />;
        case 'video': return <OndemandVideoIcon sx={{ color: COLORS.primary }} />;
        default: return <AttachFileIcon />;
      }
    };

    return (
      <List sx={{ pt: 0 }}>
        {selectedMaterials.files.map((file, index) => {
          const fileName = file.split('/').pop();
          return (
            <ListItem 
              key={index} 
              button 
              onClick={() => window.open(file, '_blank')}
              sx={{
                borderRadius: 2,
                mb: 1,
                border: `2px solid ${alpha(COLORS.primary, 0.1)}`,
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: alpha(COLORS.primary, 0.08),
                  borderColor: alpha(COLORS.primary, 0.3),
                  transform: 'translateX(4px)'
                }
              }}
            >
              <ListItemIcon>
                {getIcon(selectedMaterials.type)}
              </ListItemIcon>
              <ListItemText 
                primary={fileName}
                primaryTypographyProps={{
                  fontWeight: 600,
                  color: COLORS.dark
                }}
              />
            </ListItem>
          );
        })}
      </List>
    );
  };

  const renderCourseCard = (course) => {
    const theme = getCategoryTheme(course.category);
    const hasPdf = course.course_pdf_path && course.course_pdf_path !== '[]';
    const hasImage = course.course_image_path && course.course_image_path !== '[]';
    const hasVideo = course.course_video_path && course.course_video_path !== '[]';

    return (
      <Card 
        elevation={0}
        sx={{ 
          height: '100%', 
          display: 'flex', 
          flexDirection: 'column',
          borderRadius: 4,
          border: '2px solid',
          borderColor: alpha(theme.color, 0.15),
          overflow: 'hidden',
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-8px)',
            boxShadow: `0 20px 40px ${alpha(theme.color, 0.2)}`,
            borderColor: alpha(theme.color, 0.4),
            '& .course-image': {
              transform: 'scale(1.05)'
            }
          }
        }}
      >
        {/* Image container */}
        <Box sx={{ position: 'relative', overflow: 'hidden' }}>
          <CardMedia
            className="course-image"
            component="img"
            height="140"
            image={course.cover_image_path ? getFileUrl(course.cover_image_path)?.[0] : '/placeholder-image.jpg'}
            alt={course.title}
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = '/placeholder-image.jpg';
            }}
            sx={{
              transition: 'transform 0.4s ease'
            }}
          />
          {/* Category badge */}
          <Chip
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <span>{theme.icon}</span>
                <span>{course.category}</span>
              </Box>
            }
            size="small"
            sx={{
              position: 'absolute',
              top: 12,
              left: 12,
              backgroundColor: 'rgba(255,255,255,0.95)',
              backdropFilter: 'blur(10px)',
              color: theme.color,
              fontWeight: 700,
              fontSize: '0.75rem',
              border: 'none',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
            }}
          />
        </Box>

        <CardContent sx={{ flexGrow: 1, p: 3 }}>
          <Typography 
            variant="h6" 
            sx={{ 
              fontWeight: 800,
              color: COLORS.dark,
              mb: 1,
              lineHeight: 1.3,
              fontSize: '1.1rem'
            }}
          >
            {course.title}
          </Typography>
          
          <Typography 
            variant="body2" 
            sx={{ 
              color: '#636e72',
              mb: 2,
              lineHeight: 1.6,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden'
            }}
          >
            {course.description}
          </Typography>

          {/* Action button */}
          <Button
            variant="contained"
            disableElevation
            fullWidth
            startIcon={<PlayArrowIcon />}
            component={Link}
            to={`/course/${course.id}/view`}
            sx={{
              py: 1.2,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 700,
              background: `linear-gradient(135deg, ${theme.color} 0%, ${alpha(theme.color, 0.85)} 100%)`,
              boxShadow: `0 4px 14px ${alpha(theme.color, 0.35)}`,
              '&:hover': {
                boxShadow: `0 6px 20px ${alpha(theme.color, 0.45)}`,
              }
            }}
          >
            Comenzar curso
          </Button>
        </CardContent>

        {/* Materials footer */}
        {(hasPdf || hasImage || hasVideo) && (
          <Box 
            sx={{ 
              px: 3, 
              py: 2, 
              borderTop: `2px solid ${alpha(theme.color, 0.1)}`,
              backgroundColor: alpha(theme.color, 0.03),
              display: 'flex', 
              justifyContent: 'center',
              gap: 1
            }}
          >
            {hasPdf && (
              <Chip
                icon={<PictureAsPdfIcon />}
                label="PDF"
                size="small"
                onClick={() => handleOpenMaterials(getFileUrl(course.course_pdf_path), 'pdf')}
                sx={{
                  backgroundColor: alpha(COLORS.error, 0.1),
                  color: COLORS.error,
                  fontWeight: 600,
                  cursor: 'pointer',
                  border: 'none',
                  '& .MuiChip-icon': { color: COLORS.error },
                  '&:hover': {
                    backgroundColor: alpha(COLORS.error, 0.2),
                  }
                }}
              />
            )}
            {hasImage && (
              <Chip
                icon={<PhotoIcon />}
                label="Fotos"
                size="small"
                onClick={() => handleOpenMaterials(getFileUrl(course.course_image_path), 'image')}
                sx={{
                  backgroundColor: alpha(COLORS.success, 0.1),
                  color: COLORS.success,
                  fontWeight: 600,
                  cursor: 'pointer',
                  border: 'none',
                  '& .MuiChip-icon': { color: COLORS.success },
                  '&:hover': {
                    backgroundColor: alpha(COLORS.success, 0.2),
                  }
                }}
              />
            )}
            {hasVideo && (
              <Chip
                icon={<VideoLibraryIcon />}
                label="Videos"
                size="small"
                onClick={() => handleOpenMaterials(getFileUrl(course.course_video_path), 'video')}
                sx={{
                  backgroundColor: alpha(COLORS.primary, 0.1),
                  color: COLORS.primary,
                  fontWeight: 600,
                  cursor: 'pointer',
                  border: 'none',
                  '& .MuiChip-icon': { color: COLORS.primary },
                  '&:hover': {
                    backgroundColor: alpha(COLORS.primary, 0.2),
                  }
                }}
              />
            )}
          </Box>
        )}
      </Card>
    );
  };

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (loading) {
    return (
      <Box 
        sx={{ 
          minHeight: '100vh',
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'center', 
          alignItems: 'center',
          background: 'linear-gradient(180deg, #f8fffe 0%, #f0f7ff 100%)'
        }}
      >
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: 3,
            background: `linear-gradient(135deg, ${COLORS.gradient1} 0%, ${COLORS.gradient2} 100%)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 3,
            boxShadow: `0 8px 32px ${alpha(COLORS.gradient1, 0.3)}`
          }}
        >
          <MenuBookIcon sx={{ fontSize: 40, color: 'white' }} />
        </Box>
        <Typography variant="h6" sx={{ color: COLORS.dark, fontWeight: 600, mb: 2 }}>
          Cargando tus cursos...
        </Typography>
        <Box sx={{ width: 200 }}>
          <LinearProgress 
            sx={{ 
              borderRadius: 2,
              height: 6,
              backgroundColor: alpha(COLORS.gradient1, 0.2),
              '& .MuiLinearProgress-bar': {
                background: `linear-gradient(90deg, ${COLORS.gradient1} 0%, ${COLORS.gradient2} 100%)`,
                borderRadius: 2
              }
            }} 
          />
        </Box>
      </Box>
    );
  }

  return (
    <Box 
      sx={{ 
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #f8fffe 0%, #f0f7ff 100%)'
      }}
    >
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Header */}
        <Box
          sx={{
            background: `linear-gradient(135deg, ${COLORS.gradient1} 0%, ${COLORS.gradient2} 100%)`,
            borderRadius: 4,
            p: { xs: 3, md: 4 },
            mb: 4,
            position: 'relative',
            overflow: 'hidden',
            boxShadow: `0 20px 60px ${alpha(COLORS.gradient1, 0.3)}`
          }}
        >
          {/* Decorative elements */}
          <Box
            sx={{
              position: 'absolute',
              top: -40,
              right: -40,
              width: 150,
              height: 150,
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.1)',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: -20,
              left: '40%',
              width: 100,
              height: 100,
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.08)',
            }}
          />
          
          <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: 3,
                backgroundColor: 'rgba(255,255,255,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <PersonIcon sx={{ fontSize: 32, color: 'white' }} />
            </Box>
            <Box>
              <Typography 
                variant="h4" 
                sx={{ 
                  color: 'white',
                  fontWeight: 900,
                  letterSpacing: -0.5
                }}
              >
                Mis Cursos
              </Typography>
              <Typography 
                sx={{ 
                  color: 'rgba(255,255,255,0.85)',
                  fontWeight: 500
                }}
              >
                {courses.length > 0 
                  ? `${courses.length} cursos asignados · ${filteredCourses.length} mostrados`
                  : 'No tienes cursos asignados'
                }
              </Typography>
            </Box>
          </Box>
        </Box>

        {courses.length === 0 ? (
          /* Empty State */
          <Paper
            elevation={0}
            sx={{
              p: 6,
              textAlign: 'center',
              borderRadius: 4,
              border: '2px dashed',
              borderColor: alpha(COLORS.primary, 0.2),
              backgroundColor: alpha(COLORS.primary, 0.02)
            }}
          >
            <SchoolIcon sx={{ fontSize: 80, color: alpha(COLORS.primary, 0.3), mb: 2 }} />
            <Typography variant="h5" sx={{ color: COLORS.dark, fontWeight: 700, mb: 1 }}>
              No tienes cursos asignados
            </Typography>
            <Typography variant="body1" sx={{ color: '#636e72', mb: 3 }}>
              Contacta con tu supervisor para que te asigne cursos de formación
            </Typography>
            <Button
              variant="contained"
              component={Link}
              to="/"
              sx={{
                background: `linear-gradient(135deg, ${COLORS.gradient1} 0%, ${COLORS.gradient2} 100%)`,
                fontWeight: 700,
                px: 4,
                py: 1.5,
                borderRadius: 2
              }}
            >
              Volver al inicio
            </Button>
          </Paper>
        ) : (
          <>
            {/* Filters Bar */}
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                mb: 4,
                borderRadius: 3,
                border: '2px solid #e0e0e0',
                background: 'linear-gradient(135deg, #ffffff 0%, #f8fffe 100%)'
              }}
            >
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                {/* Search */}
                <TextField
                  placeholder="Buscar curso..."
                  variant="outlined"
                  size="small"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: COLORS.gradient1 }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    flexGrow: 1,
                    minWidth: '200px',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': { borderColor: COLORS.gradient1 },
                      '&.Mui-focused fieldset': { borderColor: COLORS.gradient1 }
                    }
                  }}
                />

                {/* Category Filter */}
                <FormControl size="small" sx={{ minWidth: '180px' }}>
                  <InputLabel>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <CategoryIcon sx={{ fontSize: 18 }} />
                      Categoría
                    </Box>
                  </InputLabel>
                  <Select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    label="Categoría xxxxx"
                    sx={selectStyles(COLORS.primary)}
                    MenuProps={menuProps(COLORS.primary)}
                    renderValue={(value) => (
                      <Chip
                        label={value || 'Todas'}
                        size="small"
                        sx={{
                          backgroundColor: alpha(value ? getCategoryTheme(value).color : COLORS.info, 0.15),
                          color: value ? getCategoryTheme(value).color : COLORS.info,
                          fontWeight: 600,
                          border: 'none'
                        }}
                      />
                    )}
                  >
                    <MenuItem value="">
                      <Chip
                        label="Todas las categorías"
                        size="small"
                        sx={{
                          backgroundColor: alpha(COLORS.info, 0.15),
                          color: COLORS.info,
                          fontWeight: 600,
                          border: 'none'
                        }}
                      />
                    </MenuItem>
                    {Object.entries(CATEGORY_COLORS).map(([cat, theme]) => (
                      <MenuItem key={cat} value={cat}>
                        <Chip
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <span>{theme.icon}</span>
                              <span>{cat}</span>
                            </Box>
                          }
                          size="small"
                          sx={{
                            backgroundColor: alpha(theme.color, 0.15),
                            color: theme.color,
                            fontWeight: 600,
                            border: 'none'
                          }}
                        />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Sort Order */}
                <FormControl size="small" sx={{ minWidth: '200px' }}>
                  <InputLabel>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <SortIcon sx={{ fontSize: 18 }} />
                      Ordenar por
                    </Box>
                  </InputLabel>
                  <Select
                    value={sortOrder}
                    onChange={(e) => setSortOrder(e.target.value)}
                    label="Ordenar por xxxxx"
                    sx={selectStyles(COLORS.gradient2)}
                    MenuProps={menuProps(COLORS.gradient2)}
                  >
                    <MenuItem value="desc">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TrendingUpIcon sx={{ fontSize: 18, color: COLORS.gradient2 }} />
                        Más recientes primero
                      </Box>
                    </MenuItem>
                    <MenuItem value="asc">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AccessTimeIcon sx={{ fontSize: 18, color: COLORS.gradient2 }} />
                        Más antiguos primero
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>

                {/* Attachments Toggle */}
                <FormControlLabel
                  control={
                    <Switch
                      checked={hasAttachments}
                      onChange={(e) => setHasAttachments(e.target.checked)}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: COLORS.gradient1
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: COLORS.gradient1
                        }
                      }}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <AttachFileIcon sx={{ fontSize: 18, color: '#636e72' }} />
                      <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, color: '#636e72' }}>
                        Con adjuntos
                      </Typography>
                    </Box>
                  }
                />
              </Box>
            </Paper>

            {/* Courses Grid */}
            <Grid container spacing={3}>
              {paginatedCourses.map((course) => (
                <Grid item xs={12} sm={6} md={3} key={course.id}>
                  {renderCourseCard(course)}
                </Grid>
              ))}
            </Grid>

            {/* Empty filtered state */}
            {filteredCourses.length === 0 && courses.length > 0 && (
              <Paper
                elevation={0}
                sx={{
                  p: 6,
                  textAlign: 'center',
                  borderRadius: 4,
                  border: '2px dashed',
                  borderColor: alpha(COLORS.primary, 0.2),
                  backgroundColor: alpha(COLORS.primary, 0.02)
                }}
              >
                <SearchIcon sx={{ fontSize: 64, color: alpha(COLORS.primary, 0.3), mb: 2 }} />
                <Typography variant="h6" sx={{ color: '#636e72', fontWeight: 600 }}>
                  No se encontraron cursos
                </Typography>
                <Typography variant="body2" sx={{ color: '#b2bec3', mt: 1 }}>
                  Prueba a modificar los filtros de búsqueda
                </Typography>
              </Paper>
            )}

            {/* Pagination */}
            {filteredCourses.length > 0 && (
              <Box sx={{ mt: 5, display: 'flex', justifyContent: 'center' }}>
                <Pagination 
                  count={Math.ceil(filteredCourses.length / coursesPerPage)} 
                  page={page} 
                  onChange={handlePageChange}
                  size="large"
                  sx={{
                    '& .MuiPaginationItem-root': {
                      fontWeight: 600,
                      borderRadius: 2,
                      '&.Mui-selected': {
                        background: `linear-gradient(135deg, ${COLORS.gradient1} 0%, ${COLORS.gradient2} 100%)`,
                        color: 'white',
                        '&:hover': {
                          background: `linear-gradient(135deg, ${COLORS.gradient1} 0%, ${COLORS.gradient2} 100%)`,
                        }
                      }
                    }
                  }}
                />
              </Box>
            )}
          </>
        )}

        {/* Materials Dialog */}
        <Dialog 
          open={openDialog} 
          onClose={handleCloseDialog} 
          maxWidth="sm" 
          fullWidth
          fullScreen={fullscreen}
          PaperProps={{
            sx: {
              borderRadius: fullscreen ? 0 : 4,
              boxShadow: '0 24px 48px rgba(0,0,0,0.2)'
            }
          }}
        >
          <DialogTitle
            sx={{
              background: `linear-gradient(135deg, ${COLORS.gradient1} 0%, ${COLORS.gradient2} 100%)`,
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              pr: 1
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <AttachFileIcon />
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Material del curso
              </Typography>
            </Box>
            <Box>
              <IconButton
                onClick={toggleFullscreen}
                sx={{ color: 'white', mr: 0.5 }}
              >
                <FullscreenIcon />
              </IconButton>
              <IconButton
                onClick={handleCloseDialog}
                sx={{ color: 'white' }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            {renderMaterialContent()}
          </DialogContent>
        </Dialog>
      </Container>
    </Box>
  );
};

export default CursosPerfilPage;